import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  LayoutDashboard, Coins, Cog, TrendingUp, Users,
  Headphones, ShieldCheck, FileBarChart2, Award,
  ArrowUpRight,
} from "lucide-react";

const CARDS = [
  { Icon: LayoutDashboard, title: "Executive Dashboards", copy: "One-screen view of the whole organisation for CEOs and boards." },
  { Icon: Coins, title: "Finance Intelligence", copy: "Revenue, expenses, cash flow, budgets — live and accurate." },
  { Icon: Cog, title: "Operations Intelligence", copy: "Productivity, throughput, inventory and workflow efficiency." },
  { Icon: TrendingUp, title: "Sales Intelligence", copy: "Pipeline, campaigns, customer trends and revenue growth." },
  { Icon: Users, title: "HR Intelligence", copy: "Workforce performance, attendance, recruitment and retention." },
  { Icon: Headphones, title: "Customer Intelligence", copy: "Satisfaction, complaints, response times, service delivery." },
  { Icon: ShieldCheck, title: "Compliance Intelligence", copy: "Audit actions, regulatory obligations and organisational risk." },
  { Icon: FileBarChart2, title: "Business Reporting", copy: "Automated executive and board reporting on a set cadence." },
  { Icon: Award, title: "Performance Scorecards", copy: "Strategic KPIs and balanced scorecards for every level." },
];

export default function OpsWhatWeDo() {
  return (
    <section id="services" data-testid="section-what-we-do" className="relative bg-[#F1ECE0] text-[#1A2E4A] border-t border-[#1A2E4A]/10">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 md:mb-16">
          <div>
            <div className="text-[11px] uppercase tracking-[0.25em] font-semibold text-[#1A2E4A] mb-6 inline-flex items-center gap-2">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#B8E024]" />
              What we do
            </div>
            <h2 className="font-editorial text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-3xl">
              Nine practices.
              <br />
              <span className="italic">One operating intelligence system.</span>
            </h2>
          </div>
          <Link
            to="/what-we-do"
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-semibold text-[#1A2E4A] hover:text-[#B8E024] transition-colors self-start md:self-end"
          >
            See details <ArrowUpRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {CARDS.map(({ Icon, title, copy }, i) => (
            <motion.article
              key={title}
              data-testid={`wwd-card-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.5, delay: (i % 3) * 0.06 }}
              whileHover={{ y: -4 }}
              className="group relative border border-[#1A2E4A]/15 p-6 md:p-7 bg-white hover:border-[#1A2E4A] transition-colors"
            >
              <div className="h-12 w-12 rounded-full bg-[#1A2E4A]/6 flex items-center justify-center mb-6 group-hover:bg-[#D9FF3C] transition-colors">
                <Icon size={22} strokeWidth={1.5} className="text-[#1A2E4A]" />
              </div>
              <h3 className="font-editorial text-2xl leading-tight tracking-tight mb-3">{title}</h3>
              <p className="text-sm text-[#1A2E4A]/70 leading-relaxed">{copy}</p>
              <span className="absolute bottom-0 left-0 h-[2px] w-0 bg-[#B8E024] group-hover:w-full transition-all duration-500" />
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
